/**
 * SystemApplicationStatusCheck page
 */

// React
import React, { ChangeEvent, FC, useEffect, useState } from 'react';

// Material
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Box,
  IconButton,
  Checkbox,
  useTheme,
} from '@mui/material';

// Components
import SelectBase from 'components/Select';
import SelectInput from 'components/SelectInput';
import TableHeader from 'components/TableHeader';
import SearchInput from 'components/SearchInput';
import TotalRecords from 'components/TotalRecords';
import ProcessButton from 'components/ProcessButton';
import PaginationComponent from 'components/Pagination';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import ModalEditSystem from './ModalEditSystem/modalEditSystem';
import ModalEditDateSystem from './ModalEditSystem/modalEditDateSystem';

// Data mock
import {
  menuItems,
  optionApplicationCheck,
  headerApplicationStatusCheck,
  defaultParamsApplicationSettingCheck,
} from 'initDataPages';

// Images
import { setting_icon } from 'assets/icons';

// Hooks
import useGetListData from 'hooks/useGetListData';

// Common styles
import {
  PageContainer,
  BoxNoDataList,
  CustomTableHead,
  RecordsContainer,
  CustomTableContainer,
  WrapperSearchAndButton,
  WrapperSearchAndBtnProcess,
} from 'styles';

// Utils
import { formatDate } from 'utils/helper/common';
import { DataSystemApplicationSettingCheck, MenuItems, OptionValue } from 'utils/types';
import {
  StatusKey,
  VALUE_MENU,
  SYSTEM_MENU,
  FORMAT_DATE,
  OptionBulkOperations,
  NOTIFICATION_MESSAGES,
} from 'utils/constants';

// Services
import SystemService from 'services/SystemService';
import dayjs from 'dayjs';

// Component
const SystemApplicationStatusCheck: FC = () => {
  const {
    state,
    perPage,
    dataList,
    isLoading,
    serviceId,
    totalPages,
    currentPage,
    totalRecords,
    paramsGetList,
    stateInformation,
    scrollContainerRef,
    getIndex,
    setNotify,
    isSortable,
    handleEdit,
    getListData,
    setIsLoading,
    handleExport,
    handleCloseEdit,
    handleChangePage,
    setParamsGetList,
    handleRequestSort,
    setStateInformation,
    handleSelectByService,
  } = useGetListData<DataSystemApplicationSettingCheck>(
    (paramsGetList) => SystemService.getSystemApplicationSettingCheck(paramsGetList),
    (paramsGetList) => SystemService.exportSystemApplicationSettingCheck(paramsGetList),
    undefined,
    defaultParamsApplicationSettingCheck,
    SYSTEM_MENU.DOWNLOAD_SYSTEM_APPLICATION_SETTING_CHECK,
    NOTIFICATION_MESSAGES.ERROR.DOWNLOAD
  );

  const theme = useTheme();
  const [typeUpdate, setTypeUpdate] = useState<OptionValue>(OptionBulkOperations.DEFAULT);
  const [services, setServices] = useState<MenuItems<any>>([{ value: null, content: '全て' }]);
  const [isOpenModalBulkUpdate, setIsOpenModalBulkUpdate] = useState(false);
  const [isOpenModalBulkUpdateDate, setIsOpenModalBulkUpdateDate] = useState(false);
  const [itemsChecked, setItemsChecked] = useState<{ [key: number]: string[] }>({});
  const [listCreatedAtChecked, setListCreatedAtChecked] = useState<{ [key: number]: any[] }>({});
  const [startDateBulkUpdate, setStartDateBulkUpdate] = useState<Date | null>(null);
  const [endDateBulkUpdate, setEndDateBulkUpdate] = useState<Date | null>(null);

  const handleCloseModalBulkUpdate = () => {
    setTypeUpdate(OptionBulkOperations.DEFAULT);
    setIsOpenModalBulkUpdate(false);
    setIsOpenModalBulkUpdateDate(false);
  };

  const handelBulkUpdate = async () => {
    try {
      setIsLoading(true);

      const selectedIds = Object.values(itemsChecked).flat();
      const dataBulkUpdate = {
        ids: selectedIds,
        status:
          typeUpdate === OptionBulkOperations.APPROVAL ? StatusKey.IN_USED : StatusKey.CANCELED,
        start_date: startDateBulkUpdate
          ? dayjs(startDateBulkUpdate).format(FORMAT_DATE)?.toString()
          : null,
        end_date: endDateBulkUpdate
          ? dayjs(endDateBulkUpdate).format(FORMAT_DATE)?.toString()
          : null,
      };
      await SystemService.updateBulkServiceUserStatus(dataBulkUpdate);
      setNotify({
        open: true,
        title:
          typeUpdate === OptionBulkOperations.APPROVAL
            ? NOTIFICATION_MESSAGES.SUCCESS.UPDATE_APPROVED
            : NOTIFICATION_MESSAGES.SUCCESS.UPDATE_DISAPPROVED,
      });
      await getListData();
      setParamsGetList(defaultParamsApplicationSettingCheck);
      setItemsChecked({});
      setListCreatedAtChecked({});
    } catch (error) {
      setNotify({
        open: true,
        title:
          typeUpdate === OptionBulkOperations.APPROVAL
            ? NOTIFICATION_MESSAGES.ERROR.UPDATE_APPROVED
            : NOTIFICATION_MESSAGES.ERROR.UPDATE_DISAPPROVED,
      });
      console.error(error);
    } finally {
      setIsLoading(false);
      setIsOpenModalBulkUpdate(false);
      setTypeUpdate(OptionBulkOperations.DEFAULT);
    }
  };

  const getMessageTitleModalBulkUpdate = (): string => {
    return typeUpdate === OptionBulkOperations.APPROVAL
      ? NOTIFICATION_MESSAGES.QUESTION.APPROVE
      : NOTIFICATION_MESSAGES.QUESTION.DISAPPROVED;
  };

  const handleSelectTypeUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as any;
    setTypeUpdate(value);
    if (value === OptionBulkOperations.APPROVAL) {
      setIsOpenModalBulkUpdateDate(true);
    } else {
      setIsOpenModalBulkUpdate(true);
    }
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    setItemsChecked((prevItemsChecked) => ({
      ...prevItemsChecked,
      [currentPage]:
        event.target.checked && dataList ? dataList.map((item) => item.id.toString()) : [],
    }));

    setListCreatedAtChecked((prevListCreatedAtChecked) => ({
      ...prevListCreatedAtChecked,
      [currentPage]:
        event.target.checked && dataList ? dataList.map((item) => item.created_at) : [],
    }));
  };

  const handleClick = (id: number, createdAt: any) => {
    let isChecked = false;
    setItemsChecked((prevItemsChecked) => {
      const itemCurrentCheck = prevItemsChecked[currentPage];
      isChecked = (itemCurrentCheck || []).includes(id.toString());
      const updatedCheckedItems = isChecked
        ? (itemCurrentCheck || []).filter((itemId) => itemId !== id.toString())
        : [...(itemCurrentCheck || []), id.toString()];

      return {
        ...prevItemsChecked,
        [currentPage]: updatedCheckedItems,
      };
    });

    setListCreatedAtChecked((prevListCreatedAtChecked) => {
      const createdAtCurrentCheck = prevListCreatedAtChecked[currentPage] || [];
      const isStartDateChecked = createdAtCurrentCheck.includes(createdAt);

      let updatedCreatedAtItems;
      if (isStartDateChecked && isChecked) {
        const indexToRemove = createdAtCurrentCheck.indexOf(createdAt);
        updatedCreatedAtItems = [
          ...createdAtCurrentCheck.slice(0, indexToRemove),
          ...createdAtCurrentCheck.slice(indexToRemove + 1),
        ];
      } else {
        updatedCreatedAtItems = [...createdAtCurrentCheck, createdAt];
      }

      return {
        ...prevListCreatedAtChecked,
        [currentPage]: updatedCreatedAtItems,
      };
    });
  };

  useEffect(() => {
    setItemsChecked({});
  }, [
    paramsGetList.order_by,
    paramsGetList.keyword,
    paramsGetList.sort_name,
    paramsGetList.service_id,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const res = await SystemService.getSystemApplicationSetting();
        const menuItems = res
          ?.filter((item: any) => item.is_displayed === 1)
          .map((item: any) => ({
            value: item.id,
            content: item.title,
          }));

        setServices((prev) => [...prev, ...(menuItems || [])]);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  return (
    <PageContainer>
      <ModalEditSystem
        handleClose={handleCloseEdit}
        open={state?.openEdit ?? false}
        defaultValue={stateInformation}
        handleUpdateSuccess={async () => await getListData()}
      />
      <ModalEditDateSystem
        handleClose={handleCloseModalBulkUpdate}
        setOpenModelConfirm={setIsOpenModalBulkUpdate}
        setIsOpenModalBulkUpdateDate={setIsOpenModalBulkUpdateDate}
        setStartDateBulkUpdate={setStartDateBulkUpdate}
        setEndDateBulkUpdate={setEndDateBulkUpdate}
        open={isOpenModalBulkUpdateDate}
        listCreatedAtChecked={Object.values(listCreatedAtChecked).flat()}
      />
      <ConfirmDeleteModal
        open={isOpenModalBulkUpdate}
        handleConfirmDelete={handelBulkUpdate}
        handleClose={handleCloseModalBulkUpdate}
        messages={getMessageTitleModalBulkUpdate()}
      />
      <SelectInput menuItems={menuItems} defaultValue={VALUE_MENU.APPLICATION_STATUS_CHECK} />
      <RecordsContainer type="secondary">
        <WrapperSearchAndButton
          type="secondary"
          mb="-40px"
          alignItems="center !important"
          justifyContent="start !important"
        >
          <TotalRecords
            recordsPerPage={perPage}
            currentPage={currentPage}
            totalRecords={totalRecords}
          />
          <SelectBase
            value={typeUpdate}
            onChange={handleSelectTypeUpdate}
            menuItems={optionApplicationCheck}
            sx={{ mb: 0, width: '140px', ml: '20px' }}
            sxSelectItem={{ fontSize: '14px', padding: '10px' }}
            sxSelect={{ fontSize: '16px', height: '37px', p: 0 }}
            disable={Object.values(itemsChecked).every((arr) => arr.length <= 0)}
          />
        </WrapperSearchAndButton>

        <WrapperSearchAndButton type="primary">
          <SelectBase
            value={serviceId}
            menuItems={services}
            onChange={handleSelectByService}
            sx={{
              mb: 0,
              width: '250px',
              mr: '26px',
              [theme.breakpoints.down('sm')]: {
                width: '50%',
              },
            }}
            sxSelect={{ fontSize: '16px', height: '37px' }}
            sxSelectItem={{ fontSize: '14px', height: '37px', padding: '10px' }}
          />
          <WrapperSearchAndBtnProcess>
            <SearchInput />
            <ProcessButton disabled={!totalRecords} handleDownload={handleExport} />
          </WrapperSearchAndBtnProcess>
        </WrapperSearchAndButton>
      </RecordsContainer>
      <CustomTableContainer>
        <Box className="table-container" ref={scrollContainerRef}>
          <Table aria-label="register user table">
            <CustomTableHead>
              <TableHeader
                isSortable={isSortable}
                paramsGetList={paramsGetList}
                header={headerApplicationStatusCheck}
                handleRequestSort={handleRequestSort}
              >
                <TableCell>
                  <Checkbox
                    disabled={dataList?.length === 0}
                    checked={
                      (itemsChecked[currentPage] || []).length === dataList?.length &&
                      dataList?.length > 0
                    }
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectAllClick(event)}
                  />
                </TableCell>
              </TableHeader>
            </CustomTableHead>
            <TableBody>
              {dataList && dataList?.length > 0
                ? dataList?.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Checkbox
                            onClick={() => handleClick(row?.id, row?.created_at)}
                            checked={(itemsChecked[Number(currentPage)] || []).includes(
                              row.id.toString()
                            )}
                          />
                        </TableCell>
                        <TableCell align="center">{getIndex(index)}</TableCell>
                        <TableCell align="left">
                          <Box display="flex" alignItems="center" justifyContent="start" gap="7px">
                            {row?.service_title}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <IconButton
                            onClick={() => {
                              setStateInformation(row);
                              handleEdit(row?.id);
                            }}
                          >
                            <img
                              src={setting_icon}
                              alt="edit"
                              style={{ width: '25px', height: 'auto', cursor: 'pointer' }}
                            />
                          </IconButton>
                        </TableCell>
                        <TableCell align="left">{formatDate(row?.created_at)}</TableCell>
                        <TableCell align="left">{row?.company_name || ''}</TableCell>
                        <TableCell align="left">{row?.user_department || ''}</TableCell>
                        <TableCell align="left">{row?.user_name || ''}</TableCell>
                        <TableCell align="left">{row?.user_email || ''}</TableCell>
                        <TableCell align="left">{row?.user_address || ''}</TableCell>
                        <TableCell align="left">{row?.user_company_phone_number || ''}</TableCell>
                        <TableCell align="left">{row?.user_phone_number || ''}</TableCell>
                        <TableCell align="left">{row?.description || ''}</TableCell>
                      </TableRow>
                    );
                  })
                : null}
            </TableBody>
          </Table>
          {dataList?.length === 0 && !isLoading && (
            <BoxNoDataList>{NOTIFICATION_MESSAGES.ERROR.SEARCH}</BoxNoDataList>
          )}
        </Box>
      </CustomTableContainer>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={currentPage}
        dataLength={totalRecords}
        handleChangePage={handleChangePage}
      />
    </PageContainer>
  );
};

export default SystemApplicationStatusCheck;
